import { motion } from 'framer-motion';
import { useReveal } from '../../hooks/useReveal';
import { CheckCircle2, XCircle } from 'lucide-react';

const WEBSITE_TRAITS = [
    'Looks nice on desktop',
    'A contact form somewhere',
    'No idea where leads come from',
    'Hopes referrals keep coming',
    'Owner does follow-up manually',
];

const SYSTEM_TRAITS = [
    'Guides every visitor toward action',
    'Captures + qualifies intent',
    'Tracks every lead by source & stage',
    'AI follow-up within 2 minutes',
    'Owner sees the full pipeline at a glance',
];

const ease = [0.22, 1, 0.36, 1];

export function Problem() {
    const ref = useReveal();
    return (
        <section data-testid="problem-section" className="relative section overflow-hidden">
            <div className="atmosphere opacity-50" />
            <div className="shell relative">
                <div ref={ref} className="reveal max-w-3xl">
                    <p className="eyebrow">Premise · The gap most businesses live in</p>
                    <h2 className="mt-6 font-display font-semibold text-[2.6rem] sm:text-[3.4rem] lg:text-[4.4rem] leading-[1.02] tracking-[-0.04em]">
                        <span className="text-white">A website is not a </span>
                        <span className="text-gradient-cyan">growth system.</span>
                    </h2>
                    <p className="mt-6 text-[17px] sm:text-[18px] leading-[1.65] text-slate-300">
                        Most businesses already have a site. The problem is what
                        sits behind it — or doesn't. A real growth system
                        captures attention, qualifies intent, follows up fast,
                        and gives the owner a clear view of every opportunity.
                    </p>
                </div>

                <div className="mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-7">
                    <Column
                        kind="website"
                        kicker="A · Website only"
                        title="Standalone marketing site"
                        body="Static pages and a contact form. Once a visitor lands, the rest is hope."
                        traits={WEBSITE_TRAITS}
                    />
                    <Column
                        kind="system"
                        kicker="B · Growth system"
                        title="Connected acquisition engine"
                        body="A website wired into qualification, follow-up and tracking — operating as one product."
                        traits={SYSTEM_TRAITS}
                    />
                </div>
            </div>
        </section>
    );
}

function Column({ kind, kicker, title, body, traits }) {
    const ref = useReveal();
    const isSystem = kind === 'system';
    const Icon = isSystem ? CheckCircle2 : XCircle;
    const tone = isSystem ? 'text-cyan-400' : 'text-slate-500';
    return (
        <motion.div
            ref={ref}
            data-testid={`problem-column-${kind}`}
            className="reveal relative card-glass p-7 lg:p-9"
            style={{
                background: isSystem
                    ? 'linear-gradient(160deg, rgba(6,182,212,0.07), rgba(11,18,33,0.45))'
                    : 'rgba(11,18,33,0.45)',
                borderColor: isSystem ? 'rgba(6,182,212,0.30)' : 'rgba(255,255,255,0.08)',
            }}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.8, ease }}
        >
            {isSystem && (
                <div
                    className="absolute -inset-px rounded-[1.25rem] pointer-events-none opacity-50"
                    style={{
                        background:
                            'radial-gradient(60% 50% at 100% 0%, rgba(6,182,212,0.25), transparent 60%)',
                    }}
                />
            )}
            <p className={`relative font-mono text-[10.5px] tracking-[0.24em] uppercase font-medium ${tone}`}>
                {kicker}
            </p>
            <h3 className="relative mt-5 font-display font-semibold text-[1.7rem] sm:text-[2rem] tracking-[-0.025em] text-white leading-[1.1]">
                {title}
            </h3>
            <p className="relative mt-3.5 text-[15.5px] leading-[1.65] text-slate-400 max-w-md">
                {body}
            </p>

            <ul className="relative mt-7 space-y-2.5">
                {traits.map((t) => (
                    <li key={t} className="flex items-start gap-3">
                        <Icon className={`mt-0.5 w-4 h-4 shrink-0 ${tone}`} />
                        <span className={`text-[14.5px] ${isSystem ? 'text-slate-200' : 'text-slate-400'}`}>
                            {t}
                        </span>
                    </li>
                ))}
            </ul>
        </motion.div>
    );
}
